import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import './RotaJovens.css';

// Corrigir ícones Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const CENTRO_AREIOPOLIS = [-22.6607, -48.6606];

// Criar ícone numerado para cada parada
function criarIconeNumero(numero, cor = '#52525b') {
  return L.divIcon({
    className: '',
    html: `
      <div style="
        width: 30px; height: 30px;
        background: ${cor};
        border: 2.5px solid #fff;
        border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        font-size: 12px; font-weight: 700; color: #fff;
        box-shadow: 0 2px 6px rgba(0,0,0,0.35);
      ">${numero}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -18],
  });
}

// Ícone de origem
const iconeOrigem = L.divIcon({
  className: '',
  html: `
    <div style="
      width: 34px; height: 34px;
      background: #16a34a;
      border: 3px solid #fff;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 16px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
    ">🏠</div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
  popupAnchor: [0, -20],
});

// Recentrar mapa na rota
function AjustarBounds({ pontos }) {
  const map = useMap();
  const ajustado = useRef(false);
  useEffect(() => {
    if (pontos.length > 0 && !ajustado.current) {
      const bounds = L.latLngBounds(pontos.map(p => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [40, 40] });
      ajustado.current = true;
    }
  }, [map, pontos]);
  return null;
}

const RotaJovens = () => {
  const [dadosRota, setDadosRota] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [paradaSelecionada, setParadaSelecionada] = useState(null);
  const [mostrarSemEndereco, setMostrarSemEndereco] = useState(false);
  const [origemCustom, setOrigemCustom] = useState({ lat: -22.6607, lng: -48.6606, nome: 'Centro de Areiópolis' });
  const [editandoOrigem, setEditandoOrigem] = useState(false);
  const [origemTemp, setOrigemTemp] = useState('');

  const carregarRota = useCallback(async (origem = origemCustom) => {
    try {
      setLoading(true);
      setErro(null);
      const response = await api.get(
        `/irmandade/rota-jovens?lat=${origem.lat}&lng=${origem.lng}&nome=${encodeURIComponent(origem.nome)}`
      );
      setDadosRota(response.data);
      setParadaSelecionada(null);
    } catch (err) {
      setErro('Erro ao carregar rota. Verifique se o servidor está rodando.');
    } finally {
      setLoading(false);
    }
  }, [origemCustom]);

  useEffect(() => { carregarRota(); }, [carregarRota]);

  // Gerar URL do Google Maps com waypoints
  const abrirGoogleMaps = () => {
    if (!dadosRota?.rota?.length) return;

    const { origem, rota } = dadosRota;
    const pontos = [
      `${origem.lat},${origem.lng}`,
      ...rota.map(p => `${p.lat},${p.lng}`),
      `${origem.lat},${origem.lng}` // retorno
    ];

    // Google Maps suporta até ~10 waypoints na URL de forma confiável
    // Para mais de 10, divide em múltiplos links ou usa o formato de app
    if (pontos.length <= 10) {
      const url = `https://www.google.com/maps/dir/${pontos.join('/')}`;
      window.open(url, '_blank');
    } else {
      // Usar formato do Google Maps que suporta mais waypoints
      const origin = `${origem.lat},${origem.lng}`;
      const destination = `${origem.lat},${origem.lng}`;
      const waypoints = rota.map(p => `${p.lat},${p.lng}`).join('|');
      const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
      window.open(url, '_blank');
    }
  };

  // Gerar URL para WhatsApp com lista de paradas
  const compartilharWhatsApp = () => {
    if (!dadosRota?.rota?.length) return;
    let texto = `🗺️ *Rota de Visita - Jovens*\n`;
    texto += `📍 Partida: ${dadosRota.origem.nome}\n`;
    texto += `📊 ${dadosRota.total_paradas} paradas | ${dadosRota.distancia_total_km} km\n\n`;

    dadosRota.rota.forEach((p, i) => {
      const nomes = p.jovens.map(j => j.nome.split(' ').slice(0,2).join(' ')).join(', ');
      texto += `*${i+1}.* ${nomes}\n`;
      if (p.endereco) {
        texto += `   📍 ${p.endereco.replace(', Areiópolis - SP', '')}\n`;
      }
      if (p.jovens[0]?.telefone) texto += `   📞 ${p.jovens[0].telefone}\n`;
      texto += '\n';
    });

    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank');
  };

  const salvarOrigem = () => {
    // Tentar parsear coordenadas se digitadas
    const match = origemTemp.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
    if (match) {
      const nova = { lat: parseFloat(match[1]), lng: parseFloat(match[2]), nome: 'Ponto de partida personalizado' };
      setOrigemCustom(nova);
      setEditandoOrigem(false);
      carregarRota(nova);
    } else {
      alert('Digite as coordenadas no formato: -22.6607, -48.6606');
    }
  };

  if (loading) return (
    <div className="rota-loading">
      <div className="rota-loading-spinner"></div>
      <p>Calculando rota otimizada...</p>
    </div>
  );

  if (erro) return (
    <div className="rota-erro">
      <span>⚠️</span><p>{erro}</p>
      <button onClick={() => carregarRota()}>Tentar novamente</button>
    </div>
  );

  const { origem, rota, sem_coordenadas, distancia_total_km, total_jovens } = dadosRota;

  // Pontos para o mapa
  const todosOsPontos = [{ lat: origem.lat, lng: origem.lng }, ...rota.map(p => ({ lat: p.lat, lng: p.lng }))];

  // Polyline da rota (ida e volta)
  const coordsRota = [
    [origem.lat, origem.lng],
    ...rota.map(p => [p.lat, p.lng]),
    [origem.lat, origem.lng] // retorno
  ];

  // Cores por distância (gradiente azul → vermelho)
  const corParada = (i) => {
    const hue = Math.round(240 - (i / Math.max(rota.length - 1, 1)) * 120);
    return `hsl(${hue}, 85%, 45%)`;
  };

  return (
    <div className="rota-container">
      {/* Cabeçalho */}
      <div className="rota-header">
        <div className="rota-header-titulo">
          <h1>🚗 Rota de Visita — Jovens</h1>
          <p>Sequência otimizada para visitar todos os jovens de Areiópolis</p>
        </div>

        <div className="rota-stats-bar">
          <div className="rota-stat">
            <span className="rota-stat-valor">{rota.length}</span>
            <span className="rota-stat-label">Paradas</span>
          </div>
          <div className="rota-stat">
            <span className="rota-stat-valor">{total_jovens}</span>
            <span className="rota-stat-label">Jovens</span>
          </div>
          <div className="rota-stat rota-stat-destaque">
            <span className="rota-stat-valor">{distancia_total_km}</span>
            <span className="rota-stat-label">km total</span>
          </div>
          {sem_coordenadas.length > 0 && (
            <div className="rota-stat rota-stat-warn" style={{ cursor: 'pointer' }} onClick={() => setMostrarSemEndereco(!mostrarSemEndereco)}>
              <span className="rota-stat-valor">{sem_coordenadas.length}</span>
              <span className="rota-stat-label">Sem local</span>
            </div>
          )}
        </div>

        <div className="rota-acoes">
          <button className="rota-btn rota-btn-maps" onClick={abrirGoogleMaps} title="Abrir rota no Google Maps">
            <span>🗺️</span> Google Maps
          </button>
          <button className="rota-btn rota-btn-whats" onClick={compartilharWhatsApp} title="Compartilhar lista pelo WhatsApp">
            <span>💬</span> WhatsApp
          </button>
          <button className="rota-btn rota-btn-recarregar" onClick={() => carregarRota()} title="Recalcular rota">
            🔄
          </button>
        </div>
      </div>

      {/* Ponto de partida */}
      <div className="rota-origem">
        <span className="rota-origem-icon">🟢</span>
        {editandoOrigem ? (
          <div className="rota-origem-editar">
            <input
              type="text"
              placeholder="Coordenadas: -22.6607, -48.6606"
              value={origemTemp}
              onChange={e => setOrigemTemp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && salvarOrigem()}
              autoFocus
            />
            <button onClick={salvarOrigem}>✓</button>
            <button onClick={() => setEditandoOrigem(false)}>✕</button>
          </div>
        ) : (
          <div className="rota-origem-info" onClick={() => { setOrigemTemp(`${origemCustom.lat}, ${origemCustom.lng}`); setEditandoOrigem(true); }}>
            <span className="rota-origem-nome">Partida: {origemCustom.nome}</span>
            <span className="rota-origem-coords">{origemCustom.lat.toFixed(4)}, {origemCustom.lng.toFixed(4)} · clique para alterar</span>
          </div>
        )}
      </div>

      {/* Layout: Mapa + Lista */}
      <div className="rota-layout">
        {/* Mapa */}
        <div className="rota-mapa-wrapper">
          <MapContainer center={CENTRO_AREIOPOLIS} zoom={14} className="rota-mapa-leaflet">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <AjustarBounds pontos={todosOsPontos} />

            {/* Linha da rota */}
            <Polyline
              positions={coordsRota}
              color="#52525b"
              weight={3}
              opacity={0.7}
              dashArray="8, 4"
            />

            {/* Marcador de origem */}
            <Marker position={[origem.lat, origem.lng]} icon={iconeOrigem}>
              <Popup>
                <div className="rota-popup">
                  <strong>🏠 {origem.nome}</strong>
                  <p>Ponto de partida e retorno</p>
                </div>
              </Popup>
            </Marker>

            {/* Marcadores de cada parada */}
            {rota.map((parada, i) => (
              <Marker
                key={i}
                position={[parada.lat, parada.lng]}
                icon={criarIconeNumero(parada.ordem, corParada(i))}
                eventHandlers={{ click: () => setParadaSelecionada(i) }}
              >
                <Popup className="rota-popup-wrapper">
                  <div className="rota-popup">
                    <div className="rota-popup-ordem">Parada {parada.ordem}</div>
                    {parada.jovens.map((j, ji) => (
                      <div key={ji} className="rota-popup-jovem">
                        <strong>{j.nome}</strong>
                        <span>{j.idade} anos</span>
                      </div>
                    ))}
                    {parada.endereco && (
                      <p className="rota-popup-end">
                        📍 {parada.endereco.replace(', Areiópolis - SP', '')}
                      </p>
                    )}
                    {parada.jovens[0]?.responsavel && (
                      <p className="rota-popup-resp">👤 {parada.jovens[0].responsavel}</p>
                    )}
                    {parada.jovens[0]?.telefone && (
                      <a
                        href={`https://wa.me/55${parada.jovens[0].telefone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="rota-popup-tel"
                      >
                        📞 {parada.jovens[0].telefone}
                      </a>
                    )}
                    <div className="rota-popup-dist">
                      +{parada.distancia_anterior_km} km da parada anterior
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Lista de paradas */}
        <div className="rota-lista">
          <div className="rota-lista-header">
            <h3>Sequência de visitas</h3>
          </div>

          <div className="rota-lista-scroll">
            {/* Origem */}
            <div className="rota-parada rota-parada-origem">
              <div className="rota-parada-num" style={{ background: '#16a34a' }}>🏠</div>
              <div className="rota-parada-info">
                <span className="rota-parada-nome">{origem.nome}</span>
                <span className="rota-parada-end">Ponto de partida</span>
              </div>
            </div>

            {rota.map((parada, i) => (
              <div
                key={i}
                className={`rota-parada ${paradaSelecionada === i ? 'selecionada' : ''}`}
                onClick={() => setParadaSelecionada(paradaSelecionada === i ? null : i)}
              >
                <div className="rota-parada-num" style={{ background: corParada(i) }}>
                  {parada.ordem}
                </div>
                <div className="rota-parada-info">
                  <div className="rota-parada-jovens">
                    {parada.jovens.map((j, ji) => (
                      <span key={ji} className="rota-parada-nome">
                        {j.nome.split(' ').slice(0, 2).join(' ')}
                        <span className="rota-parada-idade"> {j.idade}a</span>
                        {ji < parada.jovens.length - 1 && <span className="rota-parada-sep"> + </span>}
                      </span>
                    ))}
                  </div>
                  {parada.endereco && (
                    <span className="rota-parada-end">
                      {parada.endereco.replace(', Areiópolis - SP', '').substring(0, 45)}
                    </span>
                  )}
                </div>
                <span className="rota-parada-dist">+{parada.distancia_anterior_km}km</span>
              </div>
            ))}

            {/* Expansão da parada selecionada */}
            {paradaSelecionada !== null && (
              <div className="rota-detalhe">
                <h4>Parada {rota[paradaSelecionada].ordem} — Detalhes</h4>
                {rota[paradaSelecionada].jovens.map((j, ji) => (
                  <div key={ji} className="rota-detalhe-jovem">
                    <div className="rota-detalhe-linha">
                      <span className="rota-detalhe-campo">Nome</span>
                      <span className="rota-detalhe-valor">{j.nome}</span>
                    </div>
                    <div className="rota-detalhe-linha">
                      <span className="rota-detalhe-campo">Idade</span>
                      <span className="rota-detalhe-valor">{j.idade} anos</span>
                    </div>
                    {j.responsavel && (
                      <div className="rota-detalhe-linha">
                        <span className="rota-detalhe-campo">Responsável</span>
                        <span className="rota-detalhe-valor">{j.responsavel}</span>
                      </div>
                    )}
                    {j.telefone && (
                      <div className="rota-detalhe-linha">
                        <span className="rota-detalhe-campo">Telefone</span>
                        <a href={`https://wa.me/55${j.telefone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="rota-detalhe-tel">
                          📞 {j.telefone}
                        </a>
                      </div>
                    )}
                  </div>
                ))}
                {rota[paradaSelecionada].endereco && (
                  <div className="rota-detalhe-linha">
                    <span className="rota-detalhe-campo">Endereço</span>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${rota[paradaSelecionada].lat},${rota[paradaSelecionada].lng}`}
                      target="_blank"
                      rel="noreferrer"
                      className="rota-detalhe-maps"
                    >
                      📍 {rota[paradaSelecionada].endereco.replace(', Areiópolis - SP', '')}
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Retorno */}
            <div className="rota-parada rota-parada-retorno">
              <div className="rota-parada-num" style={{ background: '#16a34a' }}>🏠</div>
              <div className="rota-parada-info">
                <span className="rota-parada-nome">Retorno — {origem.nome}</span>
                <span className="rota-parada-end">+{dadosRota.distancia_retorno_km} km</span>
              </div>
              <span className="rota-parada-dist rota-dist-total">{distancia_total_km} km</span>
            </div>
          </div>
        </div>
      </div>

      {/* Painel jovens sem endereço */}
      {mostrarSemEndereco && sem_coordenadas.length > 0 && (
        <div className="rota-sem-endereco">
          <div className="rota-sem-header">
            <h3>⚠️ Jovens sem localização ({sem_coordenadas.length})</h3>
            <button onClick={() => setMostrarSemEndereco(false)}>✕</button>
          </div>
          <div className="rota-sem-lista">
            {sem_coordenadas.map((j, i) => (
              <div key={i} className="rota-sem-item">
                <span className="rota-sem-nome">{j.nome}</span>
                <span className="rota-sem-end">{j.endereco !== 'Sem endereço' ? j.endereco : '—'}</span>
                {j.telefone && (
                  <a href={`https://wa.me/55${j.telefone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="rota-sem-tel">
                    📞 {j.telefone}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RotaJovens;
